import { attributes, html, joinClasses } from 'BomdisoftComponents/html/index';
import { renderMenu } from 'BomdisoftComponents/menu/render';
import { peerOnColour } from 'BomdisoftComponents/presence/palette';
import { renderTooltip } from 'BomdisoftComponents/tooltip/render';

export type PresenceRosterPeer = {
  /** `#rrggbb`, as published on the presence wire. */
  colour: string;
  name: string;
  /** Live sessions behind this human; above one it is noted in the roster row. */
  sessions?: number;
  userId: string;
};

export const DEFAULT_AVATAR_STACK_TEST_ID = 'presence-avatar-stack';

export const initialOf = (name: string): string => {
  const first = [...name.trim()][0];
  return first ? first.toUpperCase() : '?';
};

export const toPeerStyle = (colour: string) => `--peer:${colour};--peer-on:${peerOnColour(colour)}`;

export type AvatarStackOptions = {
  isRosterOpen?: boolean;
  maxVisible?: number;
  overflowLabel: string;
  peers: readonly PresenceRosterPeer[];
  rosterId: string;
  rosterTitle: string;
  sessionsLabel?: (sessions: number) => string;
  testId?: string;
  tooltipIdOf: (key: string) => string;
};

export const renderAvatarStack = ({ isRosterOpen = false, maxVisible = 3, overflowLabel, peers, rosterId, rosterTitle, sessionsLabel, testId = DEFAULT_AVATAR_STACK_TEST_ID, tooltipIdOf }: AvatarStackOptions) => {
  if (peers.length === 0) return html``;
  const visible = peers.slice(0, maxVisible);
  const overflow = peers.length - visible.length;
  const avatars = visible.map((peer, index) =>
    renderTooltip({
      children: html`<span${attributes({ class: joinClasses('bomdisoft-avatar-stack-avatar', index > 0 && 'bomdisoft-avatar-stack-overlap'), 'data-testid': `${testId}-avatar`, style: toPeerStyle(peer.colour) })}>${initialOf(peer.name)}</span>`,
      id: tooltipIdOf(peer.userId),
      title: peer.name,
    }),
  );
  const rows = peers.map(
    peer =>
      html`<span${attributes({ class: 'bomdisoft-avatar-stack-roster-row', 'data-testid': `${testId}-roster-row`, style: toPeerStyle(peer.colour) })}><i aria-hidden="true" class="bomdisoft-avatar-stack-roster-dot"></i><span class="bomdisoft-avatar-stack-roster-name">${peer.name}</span>${sessionsLabel && (peer.sessions ?? 1) > 1 ? html`<span class="bomdisoft-avatar-stack-roster-sessions">${sessionsLabel(peer.sessions ?? 0)}</span>` : ''}</span>`,
  );
  const roster =
    overflow > 0
      ? html`${renderTooltip({
          children: html`<button${attributes({ 'aria-expanded': isRosterOpen ? 'true' : 'false', 'aria-label': overflowLabel, class: 'bomdisoft-avatar-stack-overflow bomdisoft-avatar-stack-overlap', 'data-testid': `${testId}-overflow`, type: 'button' })}>+${overflow}</button>`,
          id: tooltipIdOf('overflow'),
          title: overflowLabel,
        })}${renderMenu({ children: html`<span class="bomdisoft-avatar-stack-roster-title">${rosterTitle}</span>${rows}`, id: rosterId, label: rosterTitle, open: isRosterOpen, role: 'dialog', testId: `${testId}-roster` })}`
      : '';
  return html`<span${attributes({ class: 'bomdisoft-avatar-stack', 'data-testid': testId })}>${avatars}${roster}</span>`;
};
